import { create } from 'zustand'
import { shallow } from 'zustand/shallow'
import { convex, convexClient } from '../convexClient'
import { api, coerceSong } from '../lib/convexHelpers'
import toast from 'react-hot-toast'

const generateCommandId = () =>
  `cmd_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`

const PLAYBACK_CLIENT_STORAGE_KEY = 'karaoke_playback_client_id'
const PLAYBACK_CLIENT_SEQ_STORAGE_KEY = 'karaoke_playback_client_seq'

const getOrCreatePlaybackClientId = () => {
  if (typeof window === 'undefined') {
    return `tv_client_${Math.random().toString(36).slice(2, 10)}`
  }

  try {
    const existing = window.sessionStorage.getItem(PLAYBACK_CLIENT_STORAGE_KEY)
    if (existing) return existing
    const created = `tv_client_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`
    window.sessionStorage.setItem(PLAYBACK_CLIENT_STORAGE_KEY, created)
    return created
  } catch {
    return `tv_client_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`
  }
}

const PLAYBACK_CLIENT_ID = getOrCreatePlaybackClientId()

const getInitialPlaybackClientSeq = () => {
  if (typeof window === 'undefined') {
    return Date.now()
  }

  try {
    const raw = window.sessionStorage.getItem(PLAYBACK_CLIENT_SEQ_STORAGE_KEY)
    const parsed = raw ? Number(raw) : NaN
    if (Number.isFinite(parsed) && parsed > 0) {
      return parsed
    }
  } catch {
    // noop - fallback below
  }

  return Date.now()
}

const persistPlaybackClientSeq = (seq) => {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.setItem(PLAYBACK_CLIENT_SEQ_STORAGE_KEY, String(seq))
  } catch {
    // noop
  }
}

let playbackClientSeq = getInitialPlaybackClientSeq()
let playbackCommandQueue = Promise.resolve()
let queuedTransportIntent = null
let transportIntentLoopRunning = false
let transportIntentSeq = 0
const PLAYBACK_INTENT_TIMEOUT_MS = 5000

const getPlaybackActionErrorMessage = (error, fallback) => {
  const message = String(error?.message || error || '').toLowerCase()
  if (message.includes('stale')) {
    return 'Playback is syncing. Try again.'
  }
  return fallback
}

const enqueueSerializedPlaybackCommand = (runner) => {
  const command = playbackCommandQueue.then(runner, runner)
  playbackCommandQueue = command.catch(() => {})
  return command
}

const computeEffectiveIsPlaying = (authoritativeIsPlaying, pendingPlaybackIntent) => {
  if (pendingPlaybackIntent) {
    return pendingPlaybackIntent.targetIsPlaying
  }
  return authoritativeIsPlaying
}

/**
 * Playback store - handles current song, cursor position, and pitch
 * Manages song navigation and song data caching
 *
 * State is synced from Convex reactive queries via useConvexSync.
 * Mutations (next/previous/pitch/playback) go directly to Convex with local-first behavior.
 */
const usePlaybackStore = create((set, get) => ({
  // State
  currentSong: null,
  pitchShift: 0,
  cursor: 0,
  skipInProgress: false,
  isPlaying: false,
  playbackVersion: 0,
  playbackRevision: 0,
  authoritativeIsPlaying: false,
  authoritativePosSeconds: 0,
  authoritativeAnchorPosSec: 0,
  authoritativeAnchorServerMs: 0,
  authoritativeLastWriterClientId: null,
  authoritativeLastWriterSeq: null,
  pendingPlaybackIntent: null,
  playbackClientId: PLAYBACK_CLIENT_ID,

  // Song data cache for preloading
  songDataCache: new Map(),
  preloadedSongData: {
    previous: null,
    current: null,
    next: null
  },

  // Actions

  /**
   * Set the current song
   */
  setCurrentSong: (song) => {
    const prev = get().currentSong
    const songChanged = (!prev && song) || (prev && !song) || (prev?.id !== song?.id)
    set(state => ({
      currentSong: song,
      ...(songChanged ? { playbackVersion: state.playbackVersion + 1 } : {})
    }))
  },

  /**
   * Set cursor position
   */
  setCursor: (cursor) => {
    set({ cursor })
  },

  /**
   * Set pitch shift value
   */
  setPitchShift: (pitchShift) => {
    set({ pitchShift })
  },

  setPlaybackRevision: (playbackRevision) => {
    set({ playbackRevision: Math.max(0, playbackRevision ?? 0) })
  },

  setAuthoritativePlayback: (payload = {}) => {
    set((state) => {
      const authoritativeIsPlaying = payload.isPlaying ?? false
      const authoritativeLastWriterClientId = payload.lastWriterClientId ?? null
      const authoritativeLastWriterSeq = Number.isFinite(payload.lastWriterSeq)
        ? Number(payload.lastWriterSeq)
        : null

      let pendingPlaybackIntent = state.pendingPlaybackIntent
      if (pendingPlaybackIntent) {
        const hasPendingSeq = Number.isFinite(pendingPlaybackIntent.clientSeq)
        const commandAcknowledged =
          hasPendingSeq &&
          authoritativeLastWriterClientId === PLAYBACK_CLIENT_ID &&
          authoritativeLastWriterSeq !== null &&
          authoritativeLastWriterSeq >= pendingPlaybackIntent.clientSeq
        const intentExpired = Date.now() - pendingPlaybackIntent.requestedAtMs > PLAYBACK_INTENT_TIMEOUT_MS

        if (commandAcknowledged || intentExpired) {
          pendingPlaybackIntent = null
        }
      }

      return {
        authoritativeIsPlaying,
        authoritativePosSeconds: payload.posSeconds ?? 0,
        authoritativeAnchorPosSec: payload.anchorPosSec ?? payload.posSeconds ?? 0,
        authoritativeAnchorServerMs: payload.anchorServerMs ?? 0,
        authoritativeLastWriterClientId,
        authoritativeLastWriterSeq,
        playbackRevision: Math.max(0, payload.revision ?? state.playbackRevision ?? 0),
        pendingPlaybackIntent,
        isPlaying: computeEffectiveIsPlaying(authoritativeIsPlaying, pendingPlaybackIntent),
      }
    })
  },

  /**
   * Manual local override (used by local-only flows such as practice mode)
   */
  setIsPlaying: (isPlaying) => {
    set({ isPlaying })
  },

  runSerializedPlaybackCommand: async (roomId, mutationRef, payload = {}, options = {}) => {
    if (!roomId) {
      throw new Error('Room is still loading')
    }

    playbackClientSeq += 1
    persistPlaybackClientSeq(playbackClientSeq)

    const commandMeta = {
      roomId,
      commandId: generateCommandId(),
      clientId: PLAYBACK_CLIENT_ID,
      clientSeq: playbackClientSeq,
    }

    if (typeof options.onDispatch === 'function') {
      options.onDispatch({
        clientId: commandMeta.clientId,
        clientSeq: commandMeta.clientSeq,
      })
    }

    return enqueueSerializedPlaybackCommand(async () => {
      const args = {
        ...commandMeta,
        ...payload,
      }

      const result = await convex.mutation(mutationRef, args)
      if (typeof result?.revision === 'number') {
        set({ playbackRevision: result.revision })
      }
      return result
    })
  },

  /**
   * Load current song by ID from Convex
   */
  loadCurrentSong: async (songId) => {
    try {
      const song = await convexClient.query(api.songs.get, { id: songId })
      const coerced = coerceSong(song)
      set({ currentSong: coerced })
      return coerced
    } catch (error) {
      console.error('Failed to load current song:', error)
      return null
    }
  },

  /**
   * Adjust pitch for the room via Convex mutation
   */
  adjustPitch: async (roomId, pitchShift) => {
    if (!roomId) {
      toast.error('Room is still loading')
      return
    }

    try {
      set({ pitchShift })
      await convex.mutation(api.playback.adjustPitch, { roomId, pitchShift })
    } catch (error) {
      toast.error('Failed to adjust pitch')
      throw error
    }
  },

  /**
   * Skip to next song with optimistic update
   */
  next: async (roomId, queue, getNextSong) => {
    if (!roomId) {
      toast.error('Room is still loading')
      return
    }

    if (get().skipInProgress) {
      return
    }

    set({ skipInProgress: true })

    try {
      const { cursor, preloadedSongData } = get()
      const nextItem =
        (typeof getNextSong === 'function' && getNextSong(queue, cursor)) ||
        (typeof getNextSong === 'function' && getNextSong(cursor)) ||
        null

      if (nextItem && preloadedSongData.next) {
        set({
          cursor: cursor + 1,
          currentSong: preloadedSongData.next
        })
      }

      const result = await get().runSerializedPlaybackCommand(
        roomId,
        api.playback.nextSong
      )

      if (result?.success === false) {
        throw new Error(result?.message || 'Failed to skip to next song')
      }
    } catch (error) {
      console.error('Next song sync failed:', error)
      toast.error(getPlaybackActionErrorMessage(error, 'Could not sync next song yet'))
    } finally {
      set({ skipInProgress: false })
    }
  },

  /**
   * Go to previous song with optimistic update
   */
  previous: async (roomId, queue, getPreviousSong) => {
    if (!roomId) {
      toast.error('Room is still loading')
      return
    }

    const { cursor, preloadedSongData } = get()
    const prevItem =
      (typeof getPreviousSong === 'function' && getPreviousSong(queue, cursor)) ||
      (typeof getPreviousSong === 'function' && getPreviousSong(cursor)) ||
      null

    if (prevItem && preloadedSongData.previous) {
      set({
        cursor: cursor - 1,
        currentSong: preloadedSongData.previous
      })
    }

    try {
      const result = await get().runSerializedPlaybackCommand(
        roomId,
        api.playback.previousSong
      )

      if (result?.success === false) {
        throw new Error(result?.message || 'Failed to go to previous song')
      }
    } catch (error) {
      console.error('Previous song sync failed:', error)
      toast.error(getPlaybackActionErrorMessage(error, 'Could not sync previous song yet'))
    }
  },

  flushTransportIntentQueue: async () => {
    if (transportIntentLoopRunning) {
      return
    }

    transportIntentLoopRunning = true

    try {
      while (queuedTransportIntent) {
        const intent = queuedTransportIntent
        queuedTransportIntent = null

        const mutationRef = intent.targetIsPlaying ? api.playback.play : api.playback.pause
        const payload = {}
        if (typeof intent.posSeconds === 'number') {
          payload.posSeconds = intent.posSeconds
        }

        const rollbackPendingIntent = () => {
          set((state) => ({
            pendingPlaybackIntent: null,
            isPlaying: state.authoritativeIsPlaying,
          }))
        }

        try {
          const result = await get().runSerializedPlaybackCommand(
            intent.roomId,
            mutationRef,
            payload,
            {
              onDispatch: ({ clientSeq }) => {
                set({
                  pendingPlaybackIntent: {
                    targetIsPlaying: intent.targetIsPlaying,
                    clientSeq,
                    requestedAtMs: Date.now(),
                  },
                  isPlaying: intent.targetIsPlaying,
                })
              },
            }
          )

          if (result?.success === false) {
            throw new Error(
              result?.message ||
              (intent.targetIsPlaying ? 'Failed to start playback' : 'Failed to pause playback')
            )
          }

          intent.waiters.forEach(({ resolve }) => resolve(result ?? { success: true }))
        } catch (error) {
          const supersededByNewerIntent =
            Boolean(queuedTransportIntent) || transportIntentSeq > intent.seq

          if (!supersededByNewerIntent) {
            rollbackPendingIntent()
            intent.waiters.forEach(({ reject }) => reject(error))
          } else {
            intent.waiters.forEach(({ resolve }) =>
              resolve({ success: true, superseded: true })
            )
          }
        }
      }
    } finally {
      transportIntentLoopRunning = false
    }
  },

  queueTransportIntent: (roomId, targetIsPlaying, posSeconds = null) => {
    if (!roomId) {
      return Promise.resolve({ success: false, skipped: true })
    }

    if (!queuedTransportIntent && !transportIntentLoopRunning) {
      const state = get()
      if (!state.pendingPlaybackIntent && state.authoritativeIsPlaying === targetIsPlaying) {
        set({ isPlaying: targetIsPlaying })
        return Promise.resolve({ success: true, noop: true })
      }
    }

    transportIntentSeq += 1
    const nextIntent = {
      seq: transportIntentSeq,
      roomId,
      targetIsPlaying,
      posSeconds: typeof posSeconds === 'number' ? posSeconds : null,
      waiters: [],
    }

    return new Promise((resolve, reject) => {
      nextIntent.waiters.push({ resolve, reject })

      // Immediate local feedback; backend commands are coalesced while in-flight.
      set({
        pendingPlaybackIntent: {
          targetIsPlaying,
          clientSeq: null,
          requestedAtMs: Date.now(),
        },
        isPlaying: targetIsPlaying,
      })

      if (queuedTransportIntent && queuedTransportIntent.roomId === roomId) {
        const sameTarget = queuedTransportIntent.targetIsPlaying === targetIsPlaying
        if (sameTarget) {
          if (targetIsPlaying && typeof nextIntent.posSeconds === 'number') {
            queuedTransportIntent.posSeconds = nextIntent.posSeconds
          }
          queuedTransportIntent.waiters.push({ resolve, reject })
        } else {
          queuedTransportIntent.waiters.forEach(({ resolve: settle }) =>
            settle({ success: true, superseded: true })
          )
          queuedTransportIntent = nextIntent
        }
      } else {
        if (queuedTransportIntent) {
          queuedTransportIntent.waiters.forEach(({ resolve: settle }) =>
            settle({ success: true, superseded: true })
          )
        }
        queuedTransportIntent = nextIntent
      }

      get().flushTransportIntentQueue().catch((error) => {
        console.error('Transport intent loop failed:', error)
      })
    })
  },

  play: async (roomId, posSeconds = null) => {
    if (!roomId) {
      return
    }
    return await get().queueTransportIntent(roomId, true, posSeconds)
  },

  pause: async (roomId, posSeconds = null) => {
    if (!roomId) {
      return
    }
    return await get().queueTransportIntent(roomId, false, posSeconds)
  },

  seek: async (roomId, posSeconds, isPlaying = null) => {
    if (!roomId) {
      return
    }

    const payload = {
      posSeconds,
    }

    if (typeof isPlaying === 'boolean') {
      payload.isPlaying = isPlaying
    }

    return await get().runSerializedPlaybackCommand(roomId, api.playback.seek, payload)
  },

  songEnded: async (roomId, endedQueueItemId = null) => {
    if (!roomId) {
      return null
    }

    const args = {}
    if (endedQueueItemId) {
      args.endedQueueItemId = endedQueueItemId
    }

    return await get().runSerializedPlaybackCommand(roomId, api.playback.songEnded, args)
  },

  // Song data cache methods

  /**
   * Get song data from cache or Convex query
   */
  getSongData: async (songId) => {
    const { songDataCache } = get()
    const cached = songDataCache.get(songId)

    if (cached && !cached.loading) {
      return cached.data
    }

    if (cached && cached.loading) {
      return new Promise((resolve) => {
        const checkCache = () => {
          const updatedCache = get().songDataCache.get(songId)
          if (updatedCache && !updatedCache.loading) {
            resolve(updatedCache.data)
          } else {
            setTimeout(checkCache, 100)
          }
        }
        checkCache()
      })
    }

    set(state => ({
      songDataCache: new Map(state.songDataCache).set(songId, {
        data: null,
        loadedAt: Date.now(),
        loading: true
      })
    }))

    try {
      const song = await convexClient.query(api.songs.get, { id: songId })
      const coerced = coerceSong(song)

      set(state => ({
        songDataCache: new Map(state.songDataCache).set(songId, {
          data: coerced,
          loadedAt: Date.now(),
          loading: false
        })
      }))

      return coerced
    } catch (error) {
      console.error('Failed to load song data:', error)

      set(state => {
        const newCache = new Map(state.songDataCache)
        newCache.delete(songId)
        return { songDataCache: newCache }
      })

      throw error
    }
  },

  /**
   * Set preloaded song data
   */
  setPreloadedSongData: (preloadedData) => {
    set({ preloadedSongData: preloadedData })
  },

  /**
   * Update preloaded song data partially
   */
  updatePreloadedSongData: (updates) => {
    set(state => ({
      preloadedSongData: { ...state.preloadedSongData, ...updates }
    }))
  },

  /**
   * Clear song data cache
   */
  clearSongDataCache: () => {
    set({ songDataCache: new Map() })
  },

  /**
   * Invalidate specific song data
   */
  invalidateSongData: (songId) => {
    set(state => {
      const newCache = new Map(state.songDataCache)
      newCache.delete(songId)
      return { songDataCache: newCache }
    })
  },

  /**
   * Clear current song when queue is exhausted
   */
  clearPlaybackIfQueueExhausted: (queue, cursorPosition = null) => {
    const state = get()
    const effectiveCursor = typeof cursorPosition === 'number' ? cursorPosition : state.cursor

    const hasUpcoming = queue.some(item =>
      typeof item.position === 'number' && item.position > effectiveCursor
    )

    if (hasUpcoming) {
      return
    }

    set({
      currentSong: null,
      preloadedSongData: {
        ...state.preloadedSongData,
        current: null,
        next: null
      }
    })
  },

  /**
   * Reset store state
   */
  reset: () => {
    playbackClientSeq = getInitialPlaybackClientSeq()
    playbackCommandQueue = Promise.resolve()
    queuedTransportIntent = null
    transportIntentLoopRunning = false
    transportIntentSeq = 0
    set({
      currentSong: null,
      pitchShift: 0,
      cursor: 0,
      skipInProgress: false,
      isPlaying: false,
      playbackVersion: 0,
      playbackRevision: 0,
      authoritativeIsPlaying: false,
      authoritativePosSeconds: 0,
      authoritativeAnchorPosSec: 0,
      authoritativeAnchorServerMs: 0,
      authoritativeLastWriterClientId: null,
      authoritativeLastWriterSeq: null,
      pendingPlaybackIntent: null,
      playbackClientId: PLAYBACK_CLIENT_ID,
      songDataCache: new Map(),
      preloadedSongData: {
        previous: null,
        current: null,
        next: null
      }
    })
  }
}))

// Selector hooks for granular subscriptions

export const useCurrentSong = () => usePlaybackStore(state => state.currentSong)

export const usePitchShift = () => usePlaybackStore(state => state.pitchShift)

export const useCursor = () => usePlaybackStore(state => state.cursor)

export const useIsPlaying = () => usePlaybackStore(state => state.isPlaying)

export const usePlaybackVersion = () => usePlaybackStore(state => state.playbackVersion)

export const usePlaybackRevision = () => usePlaybackStore(state => state.playbackRevision)

export const usePlaybackState = () => usePlaybackStore(
  state => ({
    currentSong: state.currentSong,
    pitchShift: state.pitchShift,
    cursor: state.cursor,
    skipInProgress: state.skipInProgress,
    isPlaying: state.isPlaying,
    playbackVersion: state.playbackVersion,
    playbackRevision: state.playbackRevision,
    authoritativeIsPlaying: state.authoritativeIsPlaying,
    authoritativePosSeconds: state.authoritativePosSeconds,
    authoritativeAnchorPosSec: state.authoritativeAnchorPosSec,
    authoritativeAnchorServerMs: state.authoritativeAnchorServerMs,
    authoritativeLastWriterClientId: state.authoritativeLastWriterClientId,
    authoritativeLastWriterSeq: state.authoritativeLastWriterSeq,
    pendingPlaybackIntent: state.pendingPlaybackIntent,
    playbackClientId: state.playbackClientId,
  }),
  shallow
)

export const usePreloadedSongData = () => usePlaybackStore(state => state.preloadedSongData)

export default usePlaybackStore
